import { forwardRef, useImperativeHandle } from "react";
import { Box, Space, Text, Title } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { ButtonsSelect } from "../../button/ButtonsSelect";
import { useMutation } from "@apollo/client";
import { UPDATE_REMOVABLE_ITEM } from "../../../gql/items";
import { Product } from "../../../types/interfaces";
import { FullDentureOrderTypeChoices } from "../../../types/constants";
import { RemovableItemOrderType, ImprintType } from "../../../types/enums";

type OrderTypeChoiceFormData = {
  fullDentureOrderType: RemovableItemOrderType;
};

type CreateRemovableItemFullDentureOrderTypeChoiceProps = {
  product: Product;
};

export interface OrderTypeChoiceRef {
  saveData: () => Promise<void>;
}

export const CreateRemovableItemFullDentureOrderTypeChoice = forwardRef<
  OrderTypeChoiceRef,
  CreateRemovableItemFullDentureOrderTypeChoiceProps
>(({ product }, ref) => {
  const newRemovableItem = product.removableItem?.[0];

  const filteredOrderTypeChoices =
    newRemovableItem?.imprintType === ImprintType.WAX_BITE_RDV2
      ? FullDentureOrderTypeChoices.filter(
          choice => choice.value !== RemovableItemOrderType.WAX_BITE
        )
      : FullDentureOrderTypeChoices;

  const {
    control,
    watch,
    formState: { errors },
  } = useForm<OrderTypeChoiceFormData>({
    defaultValues: {
      fullDentureOrderType:
        newRemovableItem?.fullDentureOrderType ??
        RemovableItemOrderType.WAX_BITE,
    },
  });

  const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM, {
    refetchQueries: ["getOrderByUniqueAttribute"],
    awaitRefetchQueries: true,
  });

  const fullDentureOrderType = watch("fullDentureOrderType");

  const saveData = async (): Promise<void> => {
    if (!newRemovableItem || !fullDentureOrderType) return;

    await updateRemovableItem({
      variables: {
        where: { id: newRemovableItem.id },
        data: {
          fullDentureOrderType: { set: fullDentureOrderType },
        },
      },
    });
  };

  useImperativeHandle(ref, () => ({ saveData }));

  if (!newRemovableItem) {
    return (
      <div>
        <Text>
          {"Erreur sur l'ajout de produit. Veuillez contacter le support."}
        </Text>
      </div>
    );
  }

  return (
    <Box>
      <Title order={3}>Que souhaitez-vous commander ?</Title>
      <Space h="md" />
      <Controller
        name="fullDentureOrderType"
        rules={{ required: true }}
        control={control}
        render={({ field }) => (
          <ButtonsSelect
            data={filteredOrderTypeChoices}
            value={field.value}
            onChange={value => field.onChange(value)}
            numberOfColumns={1}
            align="left"
          />
        )}
      />

      {errors?.fullDentureOrderType?.type === "required" && (
        <Text size="sm" style={{ color: "red" }}>
          Veuillez sélectionner le type de commande
        </Text>
      )}

      <Space h="xl" />
    </Box>
  );
});

CreateRemovableItemFullDentureOrderTypeChoice.displayName =
  "CreateRemovableItemFullDentureOrderTypeChoice";
